/**
 * Note Composer
 *
 * Inline rich-text note entry for the CRM activity feed (HubSpot-style):
 * type a note, click "Add note", it lands in the timeline as a `note` activity.
 * Persists a per-entity draft to localStorage so an accidental navigation or
 * tab close does not lose a long note.
 */

import { useCallback, useState } from 'react';
import { StickyNote } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { RichTextEditor } from '@/components/ui/rich-text-editor';

const draftKey = (entityId: string): string => `crm-note-draft:${entityId}`;

/** Strip HTML to plain text for emptiness checks. */
function htmlToText(html: string): string {
  if (typeof document === 'undefined') return html.replace(/<[^>]+>/g, ' ');
  return new DOMParser().parseFromString(html, 'text/html').body.textContent || '';
}

interface NoteComposerProps {
  /** Used only to namespace the localStorage draft. */
  entityId: string;
  isSaving?: boolean;
  /** Receives the raw note HTML; callers derive any title/metadata they need. */
  onSubmit: (html: string) => Promise<void>;
  placeholder?: string;
}

export function NoteComposer({
  entityId,
  isSaving,
  onSubmit,
  placeholder = 'Write a note: strategy, context, next steps…',
}: NoteComposerProps) {
  const [body, setBody] = useState<string>(() => {
    try {
      return localStorage.getItem(draftKey(entityId)) || '';
    } catch {
      return '';
    }
  });

  const handleChange = useCallback(
    (html: string) => {
      setBody(html);
      try {
        if (htmlToText(html).trim()) localStorage.setItem(draftKey(entityId), html);
        else localStorage.removeItem(draftKey(entityId));
      } catch {
        /* localStorage unavailable / over quota — drafting is best-effort */
      }
    },
    [entityId],
  );

  const text = htmlToText(body).trim();
  const isEmpty = text.length === 0;

  const handleSubmit = async (): Promise<void> => {
    if (isEmpty || isSaving) return;
    await onSubmit(body);
    setBody('');
    try {
      localStorage.removeItem(draftKey(entityId));
    } catch {
      /* ignore */
    }
  };

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-3">
      <div className="mb-2 flex items-center gap-2 text-sm font-medium text-gray-700">
        <StickyNote className="h-4 w-4 text-gray-500" /> Add a note
      </div>
      <RichTextEditor
        value={body}
        onChange={handleChange}
        placeholder={placeholder}
        disabled={isSaving}
      />
      <div className="mt-2 flex justify-end">
        <Button size="sm" disabled={isEmpty || isSaving} onClick={handleSubmit}>
          {isSaving ? 'Adding…' : 'Add note'}
        </Button>
      </div>
    </div>
  );
}
